import React, { useState, FC, MouseEvent } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useApiMembersEmployeeConditionSubmissionsSubmissionRepliesCreate,
  useEditorState,
  useEditorStateLinkContentGetter,
} from "~/hooks";

import {
  EmployeeConditionSubmissionReplyDetailWithEditForm,
  EmployeeConditionSubmissionDetail,
  TextEditor,
} from "~/components/organisms";

import {
  EmployeeConditionSubmissionType,
  EmployeeConditionSubmissionReplyType,
  MentionSuggestEmployeeType,
  PreviewWithFileType,
  ApiMembersEmployeeConditionSubmissionsSubmissionRepliesIndexResponseType,
} from "~/domains";

type PropsType = {
  highlightEmployeeConditionSubmissionReplyId?: string;
  employeeConditionSubmission: EmployeeConditionSubmissionType;
  employeeConditionSubmissionReplies: EmployeeConditionSubmissionReplyType[];
  mentions: MentionSuggestEmployeeType[];
  className?: string;
  refetch: () => Promise<
    QueryObserverResult<
      ApiMembersEmployeeConditionSubmissionsSubmissionRepliesIndexResponseType,
      unknown
    >
  >;
};

export const EmployeeConditionSubmissionRepliesWithEmployeeConditionSubmission: FC<
  PropsType
> = ({
  highlightEmployeeConditionSubmissionReplyId = "",
  employeeConditionSubmission,
  employeeConditionSubmissionReplies,
  mentions,
  className = "",
  refetch,
}: PropsType) => {
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      value: editorState,
      onChange: setEditorState,
      jsonContent,
      hasText,
      setPlainText,
    },
  ] = useEditorState();
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState,
    isWatch: true,
  });

  const { mutate, isLoading } =
    useApiMembersEmployeeConditionSubmissionsSubmissionRepliesCreate();

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!hasText) {
      toast.error("テキストが空です");
      return;
    }
    const body = {
      content: jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };

    mutate(
      {
        employeeConditionSubmissionId: employeeConditionSubmission.id,
        body,
      },
      {
        onSuccess: async () => {
          setPlainText("");
          setFiles([]);
          await refetch();
        },
      },
    );
  };

  return (
    <div className={className}>
      <div className="space-y-6">
        <EmployeeConditionSubmissionDetail
          employeeConditionSubmission={employeeConditionSubmission}
          openReply={() => refetch()}
          canDelete={false}
        />
        <div>
          {employeeConditionSubmissionReplies.map((reply) => (
            <EmployeeConditionSubmissionReplyDetailWithEditForm
              key={reply.id}
              employeeConditionSubmissionReply={reply}
              mentions={mentions}
              refetch={refetch}
              isHighlight={
                reply.id === highlightEmployeeConditionSubmissionReplyId
              }
            />
          ))}
        </div>
        <TextEditor
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          editorState={editorState}
          setEditorState={setEditorState}
          files={files}
          onChangeFiles={setFiles}
          blockQuote={fetchedData}
          isEmojiPickerTop
        />
      </div>
    </div>
  );
};
